import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import schoolLogo from "./RoyalLogo.png";
import api from "../../../api/api";

/*
|--------------------------------------------------------------------------
| ROYAL PRIESTHOOD ACADEMY
| PREMIUM ROYAL BLUE • SKY BLUE • NAVY ID CARD DESIGN
|--------------------------------------------------------------------------
*/

const COLORS = {
  navy: "#061B49",
  deepNavy: "#041331",
  royalBlue: "#1746B0",
  royalBlueDark: "#10368A",
  skyBlue: "#55B8F5",
  lightSkyBlue: "#DDF2FF",
  paleBlue: "#F0F8FF",
  iceBlue: "#F7FBFF",
  white: "#FFFFFF",
  border: "#B7D8F2",
  text: "#172B4D",
  muted: "#61748D",
  silverBlue: "#E7EFF8",
};

/*
|--------------------------------------------------------------------------
| PRODUCTION-SAFE STUDENT IMAGE URL HANDLER
|--------------------------------------------------------------------------
*/

const getStudentImageUrl = (student) => {
  if (!student) return null;

  const candidates = [
    student.student_admission_image,
    student.student_image,
    student.profile_image,
    student.photo,
    student.image,
  ];

  const image = candidates.find((candidate) => {
    if (typeof candidate === "string") {
      return candidate.trim().length > 0;
    }

    return (
      candidate &&
      typeof candidate === "object" &&
      Boolean(candidate.url || candidate.image || candidate.src)
    );
  });

  if (!image) return null;

  const rawUrl =
    typeof image === "string"
      ? image.trim()
      : image.url || image.image || image.src;

  if (typeof rawUrl !== "string" || !rawUrl.trim()) {
    return null;
  }

  try {
    if (/^https?:\/\//i.test(rawUrl)) {
      return rawUrl;
    }

    const configuredBaseURL = api.defaults?.baseURL;

    if (!configuredBaseURL) {
      console.warn(
        "API baseURL is missing; cannot resolve student image:",
        rawUrl
      );
      return null;
    }

    const baseURL = new URL(
      configuredBaseURL,
      window.location.origin
    );

    const backendRoot = baseURL.pathname.replace(
      /\/api(?:\/v\d+)?\/?$/i,
      "/"
    );

    const backendOrigin = `${baseURL.origin}${backendRoot}`;

    if (rawUrl.startsWith("/")) {
      return new URL(rawUrl, baseURL.origin).href;
    }

    return new URL(rawUrl, backendOrigin).href;
  } catch (error) {
    console.error("Unable to resolve student image URL:", {
      studentId: student.id,
      image: rawUrl,
      error,
    });

    return null;
  }
};

/*
|--------------------------------------------------------------------------
| SCHOOL LEVEL LABEL
|--------------------------------------------------------------------------
*/

const SchoolLevels = () => (
  <Text style={styles.schoolLevels}>
    DAYCARE • ELEMENTARY • JUNIOR HIGH SCHOOL
  </Text>
);

/*
|--------------------------------------------------------------------------
| PREMIUM PDF STYLES
|--------------------------------------------------------------------------
*/

const styles = StyleSheet.create({
  page: {
    padding: 18,
    backgroundColor: COLORS.white,
  },

  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignContent: "space-between",
    height: "100%",
  },

  cardWrapper: {
    width: "31.7%",
    height: "31.2%",
    marginBottom: 8,
  },

  card: {
    width: "100%",
    height: "100%",
    border: `1.2pt solid ${COLORS.royalBlue}`,
    borderRadius: 9,
    overflow: "hidden",
    backgroundColor: COLORS.white,
    position: "relative",
  },

  backCard: {
    width: "100%",
    height: "100%",
    border: `1.2pt solid ${COLORS.royalBlue}`,
    borderRadius: 9,
    overflow: "hidden",
    backgroundColor: COLORS.white,
    position: "relative",
  },

  topAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: COLORS.skyBlue,
  },

  bottomAccent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: COLORS.royalBlue,
  },

  watermarkGold: {
    position: "absolute",
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: COLORS.skyBlue,
    opacity: 0.16,
    top: 53,
    right: -30,
  },

  watermarkNavy: {
    position: "absolute",
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: COLORS.royalBlue,
    opacity: 0.08,
    top: 83,
    right: 25,
  },

  watermarkSmall: {
    position: "absolute",
    width: 43,
    height: 43,
    borderRadius: 22,
    backgroundColor: COLORS.skyBlue,
    opacity: 0.17,
    bottom: 22,
    left: 42,
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT HEADER
  |--------------------------------------------------------------------------
  */

  header: {
    height: 57,
    backgroundColor: COLORS.navy,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 7,
    paddingVertical: 4,
    position: "relative",
  },

  logoContainer: {
    width: 35,
    height: 35,
    borderRadius: 8,
    backgroundColor: COLORS.white,
    border: `1.5pt solid ${COLORS.skyBlue}`,
    alignItems: "center",
    justifyContent: "center",
    padding: 3,
    marginRight: 7,
  },

  schoolLogo: {
    width: 27,
    height: 27,
    objectFit: "contain",
    borderRadius: 5,
  },

  headerText: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingRight: 2,
  },

  schoolName: {
    fontSize: 7.4,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    letterSpacing: 0.3,
  },

  schoolLevels: {
    fontSize: 4.6,
    fontWeight: "bold",
    color: COLORS.skyBlue,
    textAlign: "center",
    marginTop: 2,
    letterSpacing: 0.3,
  },

  schoolAddress: {
    fontSize: 3.8,
    color: COLORS.lightSkyBlue,
    textAlign: "center",
    marginTop: 2,
    lineHeight: 1.15,
  },

  schoolSubtitle: {
    fontSize: 4.8,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    marginTop: 2,
    letterSpacing: 0.35,
  },

  headerGoldLine: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: COLORS.skyBlue,
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT BODY
  |--------------------------------------------------------------------------
  */

  cardBody: {
    flexDirection: "row",
    flex: 1,
    paddingHorizontal: 7,
    paddingVertical: 6,
  },

  imageSection: {
    width: 67,
    alignItems: "center",
    justifyContent: "center",
  },

  imageContainer: {
    width: 61,
    height: 75,
    borderRadius: 7,
    border: `1.5pt solid ${COLORS.skyBlue}`,
    backgroundColor: COLORS.paleBlue,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },

  studentImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  noImage: {
    fontSize: 5.2,
    fontWeight: "bold",
    color: COLORS.muted,
    textAlign: "center",
  },

  photoLabel: {
    marginTop: 3,
    fontSize: 4.4,
    fontWeight: "bold",
    color: COLORS.royalBlue,
    letterSpacing: 0.5,
  },

  details: {
    flex: 1,
    marginLeft: 7,
    justifyContent: "center",
  },

  studentIdBox: {
    backgroundColor: COLORS.royalBlue,
    borderRadius: 5,
    borderLeft: `3pt solid ${COLORS.skyBlue}`,
    paddingHorizontal: 5,
    paddingVertical: 4,
    marginBottom: 5,
  },

  studentIdLabel: {
    fontSize: 4,
    fontWeight: "bold",
    color: COLORS.lightSkyBlue,
    marginBottom: 2,
    letterSpacing: 0.35,
  },

  studentId: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.white,
  },

  detailRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 3.5,
  },

  label: {
    width: 43,
    fontSize: 4.9,
    fontWeight: "bold",
    color: COLORS.royalBlue,
  },

  value: {
    flex: 1,
    fontSize: 5.8,
    fontWeight: "bold",
    color: COLORS.text,
    lineHeight: 1.15,
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT FOOTER
  |--------------------------------------------------------------------------
  */

  footer: {
    minHeight: 18,
    backgroundColor: COLORS.paleBlue,
    borderTop: `0.7pt solid ${COLORS.border}`,
    paddingHorizontal: 6,
    paddingVertical: 4,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerText: {
    maxWidth: "63%",
    fontSize: 4,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  footerYear: {
    maxWidth: "35%",
    fontSize: 4,
    fontWeight: "bold",
    color: COLORS.royalBlue,
    textAlign: "right",
  },

  /*
  |--------------------------------------------------------------------------
  | BACK HEADER
  |--------------------------------------------------------------------------
  */

  backTop: {
    height: 55,
    backgroundColor: COLORS.navy,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 7,
    paddingVertical: 4,
    position: "relative",
  },

  backLogoContainer: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: COLORS.white,
    border: `1.5pt solid ${COLORS.skyBlue}`,
    alignItems: "center",
    justifyContent: "center",
    padding: 3,
    marginRight: 7,
  },

  backLogo: {
    width: 26,
    height: 26,
    objectFit: "contain",
    borderRadius: 5,
  },

  backHeaderText: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  backTitle: {
    fontSize: 7.1,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    letterSpacing: 0.3,
  },

  backSchoolLevels: {
    fontSize: 4.5,
    fontWeight: "bold",
    color: COLORS.skyBlue,
    marginTop: 2,
    textAlign: "center",
    letterSpacing: 0.3,
  },

  backAddress: {
    fontSize: 4.2,
    color: COLORS.lightSkyBlue,
    marginTop: 2,
    textAlign: "center",
  },

  backSubtitle: {
    fontSize: 4.6,
    fontWeight: "bold",
    color: COLORS.white,
    marginTop: 2,
    textAlign: "center",
  },

  backGoldLine: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: COLORS.skyBlue,
  },

  /*
  |--------------------------------------------------------------------------
  | BACK CONTENT
  |--------------------------------------------------------------------------
  */

  backContent: {
    flex: 1,
    paddingHorizontal: 7,
    paddingVertical: 6,
    justifyContent: "space-between",
  },

  instructionBox: {
    backgroundColor: COLORS.paleBlue,
    border: `0.7pt solid ${COLORS.border}`,
    borderRadius: 6,
    padding: 5,
  },

  instructionHeading: {
    fontSize: 5.5,
    fontWeight: "bold",
    color: COLORS.royalBlue,
    textAlign: "center",
    marginBottom: 3,
    letterSpacing: 0.4,
  },

  instructions: {
    fontSize: 4.9,
    lineHeight: 1.35,
    color: COLORS.text,
    textAlign: "center",
  },

  rulesBox: {
    backgroundColor: COLORS.iceBlue,
    border: `0.7pt solid ${COLORS.border}`,
    borderLeft: `2pt solid ${COLORS.royalBlue}`,
    borderRadius: 5,
    padding: 5,
    marginTop: 4,
  },

  rulesHeading: {
    fontSize: 5.3,
    fontWeight: "bold",
    color: COLORS.royalBlue,
    textAlign: "center",
    marginBottom: 3,
    letterSpacing: 0.3,
  },

  rule: {
    fontSize: 4.65,
    color: COLORS.text,
    marginBottom: 2.1,
    lineHeight: 1.2,
  },

  /*
  |--------------------------------------------------------------------------
  | EMERGENCY CONTACT
  |--------------------------------------------------------------------------
  */

  emergencyBox: {
    marginTop: 4,
    backgroundColor: COLORS.royalBlue,
    borderRadius: 5,
    borderLeft: `3pt solid ${COLORS.skyBlue}`,
    paddingVertical: 4,
    paddingHorizontal: 5,
    alignItems: "center",
  },

  emergency: {
    fontSize: 5.2,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
  },

  emergencySubtext: {
    fontSize: 4.5,
    color: COLORS.lightSkyBlue,
    marginTop: 1.5,
    textAlign: "center",
  },

  /*
  |--------------------------------------------------------------------------
  | SIGNATURES
  |--------------------------------------------------------------------------
  */

  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 5,
  },

  signatureBlock: {
    width: "42%",
    alignItems: "center",
  },

  signature: {
    width: "100%",
    borderTop: `0.8pt solid ${COLORS.royalBlue}`,
    paddingTop: 3,
    textAlign: "center",
    fontSize: 4.4,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  /*
  |--------------------------------------------------------------------------
  | BACK FOOTER
  |--------------------------------------------------------------------------
  */

  backFooter: {
    marginTop: 4,
    paddingTop: 4,
    borderTop: `0.7pt solid ${COLORS.skyBlue}`,
    alignItems: "center",
  },

  backFooterText: {
    fontSize: 4.15,
    color: COLORS.royalBlue,
    fontWeight: "bold",
    textAlign: "center",
    letterSpacing: 0.2,
  },
});

/*
|--------------------------------------------------------------------------
| STUDENT FRONT ID CARD
|--------------------------------------------------------------------------
*/

const StudentFrontCard = ({ student, academicYear }) => {
  const year =
    student.academic_year_name ||
    academicYear?.name ||
    academicYear?.academic_year ||
    (typeof academicYear === "string" ? academicYear : null) ||
    "N/A";

  const studentPhoto = getStudentImageUrl(student);

  const studentName =
    student.student_name ||
    student.full_name ||
    student.full_name_name ||
    "N/A";

  const className =
    typeof student.grade_class === "object"
      ? student.grade_class?.name
      : student.grade_class ||
        student.grade_class_name ||
        "N/A";

  const division =
    typeof student.division === "object"
      ? student.division?.name
      : student.division ||
        student.division_assigned_name ||
        "N/A";

  return (
    <View style={styles.card}>
      <View style={styles.topAccent} />

      {/* Decorative watermarks preserved */}
      <View style={styles.watermarkGold} />
      <View style={styles.watermarkNavy} />
      <View style={styles.watermarkSmall} />

      {/* HEADER */}

      <View style={styles.header}>
        <View style={styles.logoContainer}>
          <Image src={schoolLogo} style={styles.schoolLogo} />
        </View>

        <View style={styles.headerText}>
          <Text style={styles.schoolName}>
            ROYAL PRIESTHOOD ACADEMY
          </Text>

          <SchoolLevels />

          <Text style={styles.schoolAddress}>
            New Jerusalem Community, Johnsonville, Monrovia, Liberia
          </Text>

          <Text style={styles.schoolSubtitle}>
            STUDENT IDENTIFICATION CARD
          </Text>
        </View>

        <View style={styles.headerGoldLine} />
      </View>

      {/* STUDENT INFORMATION */}

      <View style={styles.cardBody}>
        <View style={styles.imageSection}>
          <View style={styles.imageContainer}>
            {studentPhoto ? (
              <Image
                src={studentPhoto}
                style={styles.studentImage}
              />
            ) : (
              <Text style={styles.noImage}>NO PHOTO</Text>
            )}
          </View>

          <Text style={styles.photoLabel}>STUDENT</Text>
        </View>

        <View style={styles.details}>
          <View style={styles.studentIdBox}>
            <Text style={styles.studentIdLabel}>
              OFFICIAL STUDENT ID
            </Text>

            <Text style={styles.studentId}>
              {student.card_number ||
                student.admission_id ||
                "STUDENT ID"}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>NAME</Text>
            <Text style={styles.value}>{studentName}</Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>CLASS</Text>
            <Text style={styles.value}>{className}</Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>DIVISION</Text>
            <Text style={styles.value}>{division}</Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>GENDER</Text>
            <Text style={styles.value}>
              {student.gender || "N/A"}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>SCHOOL YEAR</Text>
            <Text style={styles.value}>{year}</Text>
          </View>
        </View>
      </View>

      {/* SCHOOL-BRANDED FOOTER */}

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          PROPERTY OF ROYAL PRIESTHOOD ACADEMY
        </Text>

        <Text style={styles.footerYear}>
          VALID: {year}
        </Text>
      </View>

      <View style={styles.bottomAccent} />
    </View>
  );
};

/*
|--------------------------------------------------------------------------
| CREATE NINE PRINTING SLOTS
|--------------------------------------------------------------------------
*/

const createNineSlots = (students) => {
  const slots = [...students];

  while (slots.length < 9) {
    slots.push(null);
  }

  return slots.slice(0, 9);
};

/*
|--------------------------------------------------------------------------
| STUDENT BACK ID CARD
|--------------------------------------------------------------------------
*/

const StudentBackCard = () => (
  <View style={styles.backCard}>
    <View style={styles.topAccent} />

    {/* ORIGINAL LOGO AND BACK GRAPHICS PRESERVED */}

    <View style={styles.watermarkGold} />
    <View style={styles.watermarkNavy} />
    <View style={styles.watermarkSmall} />

    <View style={styles.backTop}>
      <View style={styles.backLogoContainer}>
        <Image src={schoolLogo} style={styles.backLogo} />
      </View>

      <View style={styles.backHeaderText}>
        <Text style={styles.backTitle}>
          ROYAL PRIESTHOOD ACADEMY
        </Text>

        <Text style={styles.backSchoolLevels}>
          DAYCARE • ELEMENTARY • JUNIOR HIGH SCHOOL
        </Text>

        <Text style={styles.backAddress}>
          New Jerusalem Community, Johnsonville, Monrovia, Liberia
        </Text>

        <Text style={styles.backSubtitle}>
          STUDENT IDENTIFICATION CARD
        </Text>
      </View>

      <View style={styles.backGoldLine} />
    </View>

    <View style={styles.backContent}>
      <View style={styles.instructionBox}>
        <Text style={styles.instructionHeading}>
          IMPORTANT NOTICE
        </Text>

        <Text style={styles.instructions}>
          This identification card remains the property of
          Royal Priesthood Academy. If found, please return
          it to the school administration or the nearest
          police station.
        </Text>
      </View>

      <View style={styles.rulesBox}>
        <Text style={styles.rulesHeading}>
          CARD REGULATIONS
        </Text>

        <Text style={styles.rule}>
          • Present this card when requested by authorized
          school officials.
        </Text>

        <Text style={styles.rule}>
          • This card is valid only for the academic year
          printed on the front.
        </Text>

        <Text style={styles.rule}>
          • Do not lend or transfer this card to another person.
        </Text>

        <Text style={styles.rule}>
          • Report lost, stolen, or damaged cards to the
          school administration immediately.
        </Text>
      </View>

      <View style={styles.emergencyBox}>
        <Text style={styles.emergency}>
          EMERGENCY CONTACT
        </Text>

        <Text style={styles.emergencySubtext}>
          PLEASE CONTACT THE SCHOOL ADMINISTRATION
        </Text>
      </View>

      <View style={styles.signatureSection}>
        <View style={styles.signatureBlock}>
          <Text style={styles.signature}>
            AUTHORIZED SIGNATURE
          </Text>
        </View>

        <View style={styles.signatureBlock}>
          <Text style={styles.signature}>
            OFFICIAL SCHOOL STAMP
          </Text>
        </View>
      </View>

      <View style={styles.backFooter}>
        <Text style={styles.backFooterText}>
          CHARACTER • EDUCATION • EXCELLENCE • SERVICE
        </Text>
      </View>
    </View>

    <View style={styles.bottomAccent} />
  </View>
);

/*
|--------------------------------------------------------------------------
| MAIN PDF COMPONENT
| A4 LANDSCAPE • NINE CARDS PER SIDE • FRONT/BACK PRINTING
|--------------------------------------------------------------------------
*/

const StudentIDCardPDF = ({
  students = [],
  academicYear = null,
}) => {
  const pages = [];

  for (let index = 0; index < students.length; index += 9) {
    pages.push(students.slice(index, index + 9));
  }

  return (
    <Document
      title="Royal Priesthood Academy — Student ID Cards"
      author="Royal Priesthood Academy"
      subject="Official Student Identification Cards"
    >
      {pages.map((pageStudents, pageIndex) => {
        const slots = createNineSlots(pageStudents);

        return (
          <React.Fragment key={`card-sheet-${pageIndex}`}>
            {/* FRONT SHEET */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
            >
              <View style={styles.grid}>
                {slots.map((student, index) => (
                  <View
                    key={`front-${pageIndex}-${index}`}
                    style={styles.cardWrapper}
                  >
                    {student && (
                      <StudentFrontCard
                        student={student}
                        academicYear={academicYear}
                      />
                    )}
                  </View>
                ))}
              </View>
            </Page>

            {/* BACK SHEET */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
            >
              <View style={styles.grid}>
                {slots.map((student, index) => (
                  <View
                    key={`back-${pageIndex}-${index}`}
                    style={styles.cardWrapper}
                  >
                    {student && <StudentBackCard />}
                  </View>
                ))}
              </View>
            </Page>
          </React.Fragment>
        );
      })}
    </Document>
  );
};

export default StudentIDCardPDF;
